import React, { useState } from 'react';

export const ANALYSIS_PERIODS = [30, 60, 90, 120, 180];

function Field({ id, label, unit, help, children }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="input-unit">
        {children}
        {unit && <span>{unit}</span>}
      </div>
      {help && <div className="field-help">{help}</div>}
    </div>
  );
}

function SeedingPreferences({
  collateralPercentage,
  setCollateralPercentage,
  iskPerM3,
  setIskPerM3,
  brokerFeePercentage,
  setBrokerFeePercentage,
  salesTaxPercentage,
  setSalesTaxPercentage,
  sccPercentage,
  desiredRoiPercentage,
  setDesiredRoiPercentage,
  daysBackForPurchases,
  setDaysBackForPurchases,
  transactionTimeWindowMinutes,
  setTransactionTimeWindowMinutes,
  analysisPeriod,
  setAnalysisPeriod,
  aggregatePeriod,
  setAggregatePeriod,
  restockCadenceDays,
  setRestockCadenceDays,
  setError,
  clusterLabel,
  onPickCluster,
}) {
  const [open, setOpen] = useState(false);

  const summary = [
    ['Collateral', `${collateralPercentage}%`],
    ['Freight', `${Number(iskPerM3).toLocaleString()} ISK/m³`],
    ['Broker', `${brokerFeePercentage}%`],
    ['Tax', `${salesTaxPercentage}%`],
    ...(sccPercentage ? [['SCC', `${sccPercentage}%`]] : []),
    ['Target ROI', `${desiredRoiPercentage}%`],
    ['Analysis', `${analysisPeriod}d`],
    ['Restock', `${restockCadenceDays}d`],
    ['Cluster', clusterLabel],
  ];

  return (
    <section className="panel" aria-labelledby="prefs-title">
      <div className="panel-head">
        <h2 id="prefs-title">Pricing and forecasting</h2>
        <div className="prefs-summary">
          {summary.map(([label, value]) => (
            <span key={label}>{label} <b>{value}</b></span>
          ))}
        </div>
        <div className="panel-actions">
          <button className="btn btn-ghost" onClick={() => setOpen(o => !o)} aria-expanded={open}>
            {open ? 'Done' : 'Edit'}
          </button>
        </div>
      </div>

      {open && (
        <div className="panel-body fields">
          <div className="field-group-label">Costs</div>
          <Field id="collateralPercentage" label="Collateral" unit="%" help="Shipping cost as a share of cargo value.">
            <input
              id="collateralPercentage"
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={collateralPercentage}
              onChange={(e) => setCollateralPercentage(parseFloat(e.target.value) || 0)}
            />
          </Field>
          <Field id="iskPerM3" label="Freight rate" unit="ISK/m³" help="Shipping cost per cubic metre.">
            <input
              id="iskPerM3"
              type="number"
              min="0"
              step="50"
              value={iskPerM3}
              onChange={(e) => setIskPerM3(parseFloat(e.target.value) || 0)}
            />
          </Field>
          <Field
            id="brokerFee"
            label="Broker fee"
            unit="%"
            help={sccPercentage
              ? `Subtracted from the destination price. Don't include the ${sccPercentage}% SCC surcharge; it's added automatically at player structures.`
              : 'Subtracted from the destination price. NPC stations have no SCC surcharge.'}
          >
            <input
              id="brokerFee"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={brokerFeePercentage}
              onChange={(e) => setBrokerFeePercentage(parseFloat(e.target.value) || 0)}
            />
          </Field>
          <Field id="salesTax" label="Sales tax" unit="%" help="Subtracted from the destination price.">
            <input
              id="salesTax"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={salesTaxPercentage}
              onChange={(e) => setSalesTaxPercentage(parseFloat(e.target.value) || 0)}
            />
          </Field>

          <div className="field-group-label">Pricing</div>
          <Field id="desiredRoi" label="Target ROI" unit="%" help="Sets list prices from what you paid.">
            <input
              id="desiredRoi"
              type="number"
              min="0"
              max="1000"
              step="0.1"
              value={desiredRoiPercentage}
              onChange={(e) => setDesiredRoiPercentage(parseFloat(e.target.value) || 10)}
            />
          </Field>
          <Field id="daysBackForPurchases" label="Purchase lookback" unit="days" help="Wallet history used for purchase prices.">
            <input
              id="daysBackForPurchases"
              type="number"
              min="1"
              max="365"
              value={daysBackForPurchases}
              onChange={(e) => setDaysBackForPurchases(parseInt(e.target.value) || 30)}
            />
          </Field>
          <Field id="transactionTimeWindow" label="Transaction window" unit="min" help="Groups 30+ buys made within this window into one purchase.">
            <input
              id="transactionTimeWindow"
              type="number"
              min="1"
              max="60"
              value={transactionTimeWindowMinutes}
              onChange={(e) => setTransactionTimeWindowMinutes(parseInt(e.target.value) || 2)}
            />
          </Field>
          <div className="field">
            <label htmlFor="purchaseCluster">Purchase cluster</label>
            <button id="purchaseCluster" className="btn" onClick={onPickCluster}>
              {clusterLabel} <span className="text-faint">▾</span>
            </button>
            <div className="field-help">The shopping run used as cost basis.</div>
          </div>

          <div className="field-group-label">Forecasting</div>
          <Field id="analysisPeriod" label="Analysis period" help="History used to train the forecast.">
            <select
              id="analysisPeriod"
              value={analysisPeriod}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= aggregatePeriod) {
                  setAnalysisPeriod(value);
                  setError('');
                } else {
                  setError('Analysis period must be greater than or equal to aggregate period');
                }
              }}
            >
              {ANALYSIS_PERIODS.map(days => <option key={days} value={days}>{days} days</option>)}
              {!ANALYSIS_PERIODS.includes(analysisPeriod) && (
                <option value={analysisPeriod}>{analysisPeriod} days (current)</option>
              )}
            </select>
          </Field>
          <Field id="aggregatePeriod" label="Aggregate period" unit="days" help="Rolling window for average consumption.">
            <input
              id="aggregatePeriod"
              type="number"
              min="1"
              max="365"
              value={aggregatePeriod}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 7;
                if (value <= analysisPeriod) {
                  setAggregatePeriod(value);
                  setError('');
                } else {
                  setError('Aggregate period must be less than or equal to analysis period');
                }
              }}
            />
          </Field>
          <Field id="restockCadence" label="Restock cadence" unit="days" help="How often you haul here. Suggested targets cover this many days of sales.">
            <input
              id="restockCadence"
              type="number"
              min="1"
              max="365"
              value={restockCadenceDays}
              onChange={(e) => setRestockCadenceDays(parseInt(e.target.value) || 7)}
            />
          </Field>
        </div>
      )}
    </section>
  );
}

export default SeedingPreferences;
